import ContentIllustration from '../../components/ui/ContentIllustration';
import { useRef, useState } from 'react';
import { FileText, MessageSquareText, ArrowRight, ArrowLeft, Loader2, AlertTriangle, Clock } from 'lucide-react';
import api from '../../services/api';
import TrialIdeas from './TrialIdeas';
import AiContentNotice from './AiContentNotice';

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.docx,.txt,.csv,.html,.jpg,.jpeg,.png,.webp';

function labelFromFilename(name) {
    return name
        .replace(/\.(pdf|docx|txt|csv|html|jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, c => c.toUpperCase());
}

/**
 * Essai gratuit de la thématique personnalisable (migration 181) — parcours en étapes :
 *   1. choix : « Partir d'un document » / « Partir d'un thème » ;
 *   2. document : import du support → 20 questions ;
 *      thème : description du thème → 20 questions, avec « Des idées pour commencer » (exemples relus :
 *      idée de départ, reprise de l'idée, résultat dépliable, ajout du sujet à la thématique).
 * Crée la thématique d'essai si besoin (thematiqueId absent). Génération en tâche de fond dans les deux cas.
 * onStarted({ thematiqueId, slug, jobId, sousSujetId }) : job en file (jobId) ou exemple ajouté (sousSujetId).
 * framed : carte encadrée (page) ou à plat (déjà dans une fenêtre).
 */
export default function TrialStartCard({ thematiqueId = null, nbQuestions = 20, days = 7, onStarted, framed = true }) {
    const [step, setStep] = useState('choose'); // choose | doc | theme
    const [file, setFile] = useState(null);
    const [label, setLabel] = useState('');
    const [consignes, setConsignes] = useState('');
    const [ideaTaken, setIdeaTaken] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);
    const themeRef = useRef(null);

    const go = (s) => { setStep(s); setError(''); };

    const pickFile = (f) => {
        setError('');
        if (!f) return;
        if (f.size > MAX_SIZE) { setError('Fichier trop volumineux (10 Mo maximum).'); return; }
        setFile(f);
    };

    const sujetLabel = () => {
        const l = label.trim();
        if (l.length >= 2) return l.slice(0, 100);
        if (step === 'doc' && file) return labelFromFilename(file.name).slice(0, 100) || 'Mon sujet';
        const c = consignes.trim().replace(/\s+/g, ' ');
        return (c.length > 60 ? c.slice(0, 57).trim() + '…' : c) || 'Mon sujet';
    };

    const canSubmit = !busy && (step === 'doc' ? !!file : consignes.trim().length >= 10);

    const submit = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError('');
        try {
            const name = sujetLabel();
            let thId = thematiqueId;
            let slug = null;
            if (!thId) {
                const { data } = await api.post('/custom/thematiques.php?action=create_trial', { label: name });
                thId = data.data.id;
                slug = data.data.slug;
            }
            let jobId;
            if (step === 'doc') {
                const fd = new FormData();
                fd.append('document', file);
                fd.append('label', name);
                fd.append('thematique_id', String(thId));
                fd.append('nb_questions', String(nbQuestions));
                const { data } = await api.post('/custom/upload.php', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                jobId = data.data.job_id;
            } else {
                const { data } = await api.post('/custom/generate-from-prompt.php', {
                    thematique_id: thId, label: name, consignes: consignes.trim(), nb_questions: nbQuestions,
                });
                jobId = data.data.job_id;
            }
            onStarted?.({ thematiqueId: thId, slug, jobId });
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || 'La création n’a pas pu démarrer. Réessayez dans un instant.');
        } finally {
            setBusy(false);
        }
    };

    const trialMeta = (
        <p className="trial3-meta"><Clock size={14} /> {days} jours d’essai après la création · Sans carte bancaire</p>
    );
    const wrap = (children) => <div className={`trial3${framed ? ' trial3--framed' : ''}`}>{children}</div>;

    if (step === 'choose') {
        return wrap(<>
            <span className="trial3-eyebrow">Votre premier sujet</span>
            <h3 className="trial3-hero">Votre prochain entraînement commence ici.</h3>
            <p className="trial3-sub">{nbQuestions} questions à partir de votre support ou d’un thème que vous choisissez.</p>
            <div className="trial3-choices">
                <button type="button" className="trial3-choice" onClick={() => go('doc')}>
                    <span className="trial3-choice__icon"><ContentIllustration name="file-text" size={36} /></span>
                    <strong>Partir d’un document</strong>
                    <span>Un cours, une fiche ou un support de formation à réviser.</span>
                    <em>Importer mon document <ArrowRight size={14} /></em>
                </button>
                <button type="button" className="trial3-choice" onClick={() => go('theme')}>
                    <span className="trial3-choice__icon"><ContentIllustration name="brain" size={36} /></span>
                    <strong>Partir d’un thème</strong>
                    <span>Une idée en tête, ou l’envie de partir d’un exemple.</span>
                    <em>Décrire mon thème <ArrowRight size={14} /></em>
                </button>
            </div>
            {trialMeta}
        </>);
    }

    const isDoc = step === 'doc';
    return wrap(
        <form onSubmit={submit} className="trial3-form">
            <button type="button" className="trial3-back" onClick={() => go('choose')} disabled={busy}>
                <ArrowLeft size={14} /> Document ou thème
            </button>
            <div className="trial3-head">
                <div>
                    <h3 className="trial3-title">{isDoc ? 'Importez votre document.' : 'Que voulez-vous réviser ?'}</h3>
                    <p className="trial3-sub">{isDoc ? 'Les questions s’appuieront sur votre support.' : 'Précisez le sujet, votre objectif et le niveau souhaité.'}</p>
                </div>
                <span className="trial3-count"><strong>{nbQuestions}</strong>questions</span>
            </div>

            {isDoc ? (
                <div
                    className={`trial3-file${dragOver ? ' is-over' : ''}${file ? ' has-file' : ''}`}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
                    role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
                >
                    <span className="trial3-file__sheet" aria-hidden="true"><i /><i /><i /><i /></span>
                    <span className="trial3-file__text">
                        <strong>{file ? file.name : 'Déposez votre document ici'}</strong>
                        <span>PDF, Word, texte ou image · 10 Mo maximum</span>
                        <em>{file ? 'Changer de document' : 'Parcourir mes fichiers'} <ArrowRight size={14} /></em>
                    </span>
                    <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={(e) => pickFile(e.target.files?.[0])} />
                </div>
            ) : (
                <label className="trial3-field">
                    <span>Votre thème</span>
                    <textarea
                        ref={themeRef}
                        value={consignes}
                        onChange={(e) => { setConsignes(e.target.value); setIdeaTaken(false); }}
                        placeholder="Ex. Les institutions de la Ve République, pour un concours de catégorie B. Niveau intermédiaire."
                        rows={4}
                        maxLength={2000}
                        disabled={busy}
                    />
                </label>
            )}

            <details className="trial3-name">
                <summary>Personnaliser le nom du sujet</summary>
                <label className="trial3-field">
                    <span>Nom du sujet</span>
                    <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} disabled={busy}
                        placeholder={isDoc ? 'Repris du nom du document' : 'Repris du thème'} />
                </label>
            </details>

            {error && <p className="trial-card__error"><AlertTriangle size={14} /> {error}</p>}

            <AiContentNotice />

            <div className="trial3-submit">
                <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                    {busy ? <><Loader2 size={15} className="spin" /> Lancement…</> : <>Créer mes {nbQuestions} questions <ArrowRight size={15} /></>}
                </button>
                <span className="trial3-hint">Quelques minutes de préparation.<br />Vous pourrez fermer cette fenêtre.</span>
            </div>
            {!isDoc && ideaTaken && <p className="trial3-taken">Idée reprise : vous pouvez adapter le thème ci-dessus.</p>}

            {!isDoc && (
                <TrialIdeas
                    disabled={busy}
                    onUseIdea={({ consignes: c }) => {
                        setConsignes(c);
                        setIdeaTaken(true);
                        setTimeout(() => { themeRef.current?.focus(); themeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 50);
                    }}
                    onAdded={({ thematiqueId: thId, slug, sousSujetId }) => onStarted?.({ thematiqueId: thId, slug, jobId: null, sousSujetId })}
                />
            )}
            {trialMeta}
        </form>
    );
}
